import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import { BrandingProvider } from "./context/BrandingContext";
import Layout from "./components/Layout";
import OnboardingTour from "./components/OnboardingTour";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import Deals from "./pages/Deals";
import DealDetail from "./pages/DealDetail";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Activities from "./pages/Activities";
import Meetings from "./pages/Meetings";
import MeetingDetail from "./pages/MeetingDetail";
import Calendar from "./pages/Calendar";
import Utilization from "./pages/Utilization";
import DealFlow from "./pages/DealFlow";
import UsersPage from "./pages/Users";
import SettingsPage from "./pages/Settings";
import { Spinner } from "./components/common";

function Protected({ children, adminOnly, roles }) {
  const { user, checked } = useAuth();
  if (!checked) return <div className="min-h-screen bg-bg"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <>
      {/* Outside <Routes> on purpose: the tour walks the user across pages, so
          it has to survive the route changes it makes itself. Mounted inside a
          route's element (it used to live in Dashboard) it would unmount on the
          first navigation and take the tour with it. It renders nothing until a
          logged-in user starts it. */}
      <OnboardingTour />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Protected><Dashboard /></Protected>} />
        <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
        <Route path="/contacts/:id" element={<Protected><ContactDetail /></Protected>} />
        <Route path="/companies" element={<Protected><Companies /></Protected>} />
        <Route path="/companies/:id" element={<Protected><CompanyDetail /></Protected>} />
        <Route path="/deals" element={<Protected><Deals /></Protected>} />
        <Route path="/deals/:id" element={<Protected><DealDetail /></Protected>} />
        <Route path="/projects" element={<Protected><Projects /></Protected>} />
        <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
        <Route path="/activities" element={<Protected><Activities /></Protected>} />
        {/* Guests are excluded: a meeting summary/transcript is free text that
            can carry money figures and offhand client remarks, and free text
            can't be field-masked the way Deal.value is. The backend enforces
            the same rule (require_role("user")) -- this only keeps a guest from
            landing on a page that would 403 every request. */}
        <Route path="/meetings" element={<Protected roles={["admin", "manager", "user"]}><Meetings /></Protected>} />
        <Route path="/meetings/:id" element={<Protected roles={["admin", "manager", "user"]}><MeetingDetail /></Protected>} />
        <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
        <Route path="/utilization" element={<Protected roles={["admin", "manager"]}><Utilization /></Protected>} />
        <Route path="/deal-flow" element={<Protected roles={["admin", "manager"]}><DealFlow /></Protected>} />
        <Route path="/users" element={<Protected adminOnly><UsersPage /></Protected>} />
        {/* Both forms hit the same page: the bare path redirects to the first
            section, and :section is what makes "Settings > Branding" in the
            README and the deployment runbook an actual link. */}
        <Route path="/settings" element={<Protected adminOnly><SettingsPage /></Protected>} />
        <Route path="/settings/:section" element={<Protected adminOnly><SettingsPage /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  // BrandingProvider wraps AuthProvider, not the other way round: the login
  // screen is rendered for an *unauthenticated* viewer and still needs the
  // logo, wordmark and brand colours, so branding cannot depend on a session.
  // Both fetch on mount in parallel, so the wait is max(branding, /auth/me).
  return (
    <BrandingProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </BrandingProvider>
  );
}
